import React, { useRef, useEffect, useState } from 'react';
import { Stage, Layer, Rect, Line } from 'react-konva';
import { Box, Paper } from '@mui/material';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import { useDroppable } from '@dnd-kit/core';
import { isGroupElement, LabelElement } from '../../types';
import ElementRenderer from './ElementRenderer';
import GroupRenderer from './GroupRenderer';
import { SalesOrder } from 'services/salesOrders';
import { EDGE_OFFSET_BOTTOM, EDGE_OFFSET_RIGHT } from '../../consts';

// Grid settings
const GRID_SIZE = 12; // Size of grid cells in pixels
const PRIMARY_GRID_SIZE = GRID_SIZE; // Size of primary grid (20px)

// Margin guide settings
const MARGIN_INCHES = 0.5; // PDF export margin size in inches
const DPI = 72; // Standard DPI for pixel conversion
const MARGIN_PX = MARGIN_INCHES * DPI; // 36px margin
const TOTAL_MARGIN_EXPANSION = 2 * MARGIN_PX; // 72px total expansion (left + right or top + bottom)

// Canvas scaling settings
const SCALE_ADJUSTMENT_FACTOR = 0.91; // Scale factor to account for button space without reserving it in calculation

interface DesignerCanvasProps {
  stageRef: React.RefObject<any>;
  elements: LabelElement[];
  selectedElement: LabelElement | null;
  setSelectedElement: (element: LabelElement | null) => void;
  selectedElements: LabelElement[];
  setSelectedElements: (elements: LabelElement[]) => void;
  onUpdateElement: (id: string, updates: Partial<LabelElement>) => void;
  isDragging: boolean;
  previewMode?: boolean;
  previewData: SalesOrder | null;
  showGrid?: boolean;
  showPageBreaks?: boolean;
  showMargins?: boolean;
  canvasWidth: number;
  canvasHeight: number;
  totalPages: number;
  onAddPage?: () => Promise<void> | void;
  onRemovePage?: () => void;
  noPadding?: boolean;
  showFooter?: boolean;
  footerHeight?: number;
  footerPage?: number;
  onFooterHeightChange?: (height: number) => void;
}

const DesignerCanvas: React.FC<DesignerCanvasProps> = ({
  stageRef,
  elements,
  selectedElement,
  setSelectedElement,
  selectedElements,
  setSelectedElements,
  onUpdateElement,
  isDragging,
  previewMode = false,
  previewData,
  showGrid = true,
  showPageBreaks = true,
  showMargins = true,
  canvasWidth,
  canvasHeight,
  totalPages,
  onAddPage,
  onRemovePage,
  noPadding = false,
  showFooter = false,
  footerHeight = 80,
  footerPage,
  onFooterHeightChange,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [selectionBox, setSelectionBox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const [isSelecting, setIsSelecting] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(
    null
  );
  const layerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevTotalPagesRef = useRef(totalPages);

  // Auto-scroll to bottom when new page is added
  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  // Effect to handle auto-scroll when pages are added
  useEffect(() => {
    if (totalPages > prevTotalPagesRef.current) {
      // A page was added, scroll to bottom
      scrollToBottom();
    }
    prevTotalPagesRef.current = totalPages;
  }, [totalPages]);

  const { setNodeRef } = useDroppable({
    id: 'canvas',
  });

  // Function to handle element boundary constraints
  const handleBoundaryConstraints = (
    element: LabelElement,
    updates: Partial<LabelElement>
  ) => {
    // Create a "next" snapshot by merging element and updates
    const next = { ...element, ...updates };
    const constrainedUpdates = { ...updates };

    // Clamp width and height first against canvas bounds
    if ('width' in updates) {
      const maxWidth = canvasWidth - next.x - EDGE_OFFSET_RIGHT;
      constrainedUpdates.width = Math.max(
        1,
        Math.min(updates.width!, maxWidth)
      );
      next.width = constrainedUpdates.width;
    }
    if ('height' in updates) {
      const maxHeight = canvasHeight - next.y - EDGE_OFFSET_BOTTOM;
      constrainedUpdates.height = Math.max(
        1,
        Math.min(updates.height!, maxHeight)
      );
      next.height = constrainedUpdates.height;
    }

    // Then clamp x and y using the final clamped size values
    if ('x' in updates) {
      const newX = updates.x ?? element.x;
      const maxX = canvasWidth - next.width - EDGE_OFFSET_RIGHT;
      constrainedUpdates.x = Math.max(0, Math.min(newX, maxX));
    }
    if ('y' in updates) {
      const newY = updates.y!;
      // don't allow advanced tables to be placed within footers
      const maxY =
        showFooter && element.type === 'advancedTable'
          ? canvasHeight - footerHeight - next.height - EDGE_OFFSET_BOTTOM
          : canvasHeight - next.height - EDGE_OFFSET_BOTTOM;
      constrainedUpdates.y = Math.max(0, Math.min(newY, maxY));
    }

    return constrainedUpdates;
  };

  const getRelativePointerPosition = (stage: any) => {
    const transform = stage.getAbsoluteTransform().copy();
    transform.invert();
    const pos = stage.getPointerPosition();
    if (!pos) {
      return { x: 0, y: 0 }; // or abort the operation
    }
    const transformedPos = transform.point(pos);

    // Adjust for layer offset when margins are shown
    if (showMargins) {
      transformedPos.x -= MARGIN_PX;
      transformedPos.y -= MARGIN_PX;
    }

    return transformedPos;
  };

  const handleStageMouseDown = (e: any) => {
    if (previewMode) return;

    // Don't handle stage mouse down if we're clicking on a footer resize handle
    if (e.target.attrs.key && e.target.attrs.key.includes('footer-resize-')) {
      return;
    }

    const clickedOnEmpty =
      e.target === e.target.getStage() ||
      e.target.attrs.id === 'background-rect';

    if (clickedOnEmpty) {
      setSelectedElement(null);
      const stage = stageRef.current;
      const pos = getRelativePointerPosition(stage);

      setStartPoint(pos);
      setIsSelecting(true);
      setSelectionBox({
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0,
      });
      setSelectedElements([]);
    }
  };

  const handleStageMouseMove = () => {
    if (!isSelecting || !startPoint) return;

    const stage = stageRef.current;
    const pos = getRelativePointerPosition(stage);

    setSelectionBox({
      x: Math.min(startPoint.x, pos.x),
      y: Math.min(startPoint.y, pos.y),
      width: Math.abs(pos.x - startPoint.x),
      height: Math.abs(pos.y - startPoint.y),
    });
  };

  const handleStageMouseUp = () => {
    if (!selectionBox || !isSelecting) return;

    const selected = elements.filter((element) => {
      const elementRight = element.x + element.width;
      const elementBottom = element.y + element.height;
      const boxRight = selectionBox.x + selectionBox.width;
      const boxBottom = selectionBox.y + selectionBox.height;

      return (
        element.x < boxRight &&
        elementRight > selectionBox.x &&
        element.y < boxBottom &&
        elementBottom > selectionBox.y
      );
    });

    setSelectedElements(selected.map((el) => el));
    if (selected.length === 1) {
      setSelectedElement(selected[0]);
    }

    setIsSelecting(false);
    setSelectionBox(null);
    setStartPoint(null);
  };

  // Track initial positioning to prevent shifts during preview mode
  const initialPositionSet = useRef(false);
  const baseCanvasWidth = useRef(canvasWidth);
  const baseCanvasHeight = useRef(canvasHeight);
  const baseTotalPages = useRef(totalPages);
  const prevShowMargins = useRef(showMargins);

  // Handle margin changes synchronously to prevent scrollbar flicker
  useEffect(() => {
    if (
      prevShowMargins.current !== showMargins &&
      containerRef.current &&
      initialPositionSet.current
    ) {
      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;

      if (containerWidth > 0 && containerHeight > 0) {
        const marginExpansion = showMargins ? TOTAL_MARGIN_EXPANSION : 0;
        const referenceWidth = baseCanvasWidth.current + marginExpansion;
        const referenceHeight =
          (baseCanvasHeight.current + marginExpansion) / baseTotalPages.current;

        const scaleX = containerWidth / referenceWidth;
        const scaleY = containerHeight / referenceHeight;
        const newScale = Math.min(scaleX, scaleY, 1) * SCALE_ADJUSTMENT_FACTOR;

        setScale(newScale);
      }
    }
    prevShowMargins.current = showMargins;
  }, [showMargins]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && stageRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;

        // If container dimensions are not yet available, don't calculate scale.
        // This prevents scale from becoming 0 and rendering a 0x0 canvas.
        if (containerWidth === 0 || containerHeight === 0) {
          return;
        }

        // Use base dimensions for consistent scaling to prevent shifts during preview
        // Account for margin expansion when calculating reference dimensions for optimal fit
        const marginExpansion = showMargins ? TOTAL_MARGIN_EXPANSION : 0;
        const referenceWidth = baseCanvasWidth.current + marginExpansion;
        const referenceHeight =
          (baseCanvasHeight.current + marginExpansion) / baseTotalPages.current;

        const scaleX = containerWidth / referenceWidth;
        const scaleY = containerHeight / referenceHeight;
        const newScale = Math.min(scaleX, scaleY, 1) * SCALE_ADJUSTMENT_FACTOR; // Scale to account for button space without reserving it in calculation

        setScale(newScale);

        // Recalculate position on initial load or when not in preview mode
        // Preserve exact position when entering/staying in preview mode
        if (!initialPositionSet.current) {
          const newX = (containerWidth - referenceWidth * newScale) / 2;
          const newY = 0; // Start at top for scrolling
          setPosition({ x: newX, y: newY });
          initialPositionSet.current = true;
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [
    stageRef,
    canvasWidth,
    canvasHeight,
    totalPages,
    previewMode,
    showMargins,
  ]);

  // Update base dimensions only on initial load, not when switching modes
  useEffect(() => {
    if (!initialPositionSet.current) {
      baseCanvasWidth.current = canvasWidth;
      baseCanvasHeight.current = canvasHeight;
      baseTotalPages.current = totalPages;
    }
  }, [canvasWidth, canvasHeight, totalPages]);

  // Generate grid lines
  const gridLines = [];
  if (showGrid) {
    // Vertical lines
    for (let x = 0; x <= canvasWidth; x += GRID_SIZE) {
      const isPrimary = x % PRIMARY_GRID_SIZE === 0;
      gridLines.push(
        <Line
          key={`v${x}`}
          points={[x, 0, x, canvasHeight]}
          stroke="#ddd"
          strokeWidth={isPrimary ? 0.5 : 0.25}
          opacity={isPrimary ? 0.5 : 0.25}
          name="grid-line"
        />
      );
    }
    // Horizontal lines
    for (let y = 0; y <= canvasHeight; y += GRID_SIZE) {
      const isPrimary = y % PRIMARY_GRID_SIZE === 0;
      gridLines.push(
        <Line
          key={`h${y}`}
          points={[0, y, canvasWidth, y]}
          stroke="#ddd"
          strokeWidth={isPrimary ? 0.5 : 0.25}
          opacity={isPrimary ? 0.5 : 0.25}
          name="grid-line"
        />
      );
    }
  }

  // Add page break lines
  const pageBreakLines = [];
  if (showPageBreaks) {
    const singlePageHeight = canvasHeight / totalPages;
    for (let i = 1; i < totalPages; i++) {
      pageBreakLines.push(
        <Line
          key={`page-break-${i}`}
          points={[0, i * singlePageHeight, canvasWidth, i * singlePageHeight]}
          stroke="#2196f3"
          strokeWidth={2}
          dash={[10, 5]}
        />
      );
    }
  }

  // Add margin guides - 0.5 inch margins that will be added around existing content during PDF export
  const marginGuides = [];
  if (showMargins) {
    const singlePageHeight = canvasHeight / totalPages;

    // Add margin area indicators - only on outer document edges
    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
      const pageY = pageIndex * singlePageHeight;
      const isFirstPage = pageIndex === 0;
      const isLastPage = pageIndex === totalPages - 1;

      // Top margin strip - only on first page
      if (isFirstPage) {
        marginGuides.push(
          <Rect
            key={`margin-top`}
            x={-MARGIN_PX}
            y={pageY - MARGIN_PX}
            width={canvasWidth + 2 * MARGIN_PX}
            height={MARGIN_PX}
            fill="rgba(255, 193, 7, 0.2)" // Light amber overlay
            stroke="#ff9800"
            strokeWidth={1}
            dash={[3, 3]}
          />
        );
      }

      // Bottom margin strip - only on last page
      if (isLastPage) {
        marginGuides.push(
          <Rect
            key={`margin-bottom`}
            x={-MARGIN_PX}
            y={pageY + singlePageHeight}
            width={canvasWidth + 2 * MARGIN_PX}
            height={MARGIN_PX}
            fill="rgba(255, 193, 7, 0.2)" // Light amber overlay
            stroke="#ff9800"
            strokeWidth={1}
            dash={[3, 3]}
          />
        );
      }

      // Left margin strip - on all pages (document edge)
      marginGuides.push(
        <Rect
          key={`margin-left-${pageIndex}`}
          x={-MARGIN_PX}
          y={pageY}
          width={MARGIN_PX}
          height={singlePageHeight}
          fill="rgba(255, 193, 7, 0.2)" // Light amber overlay
          stroke="#ff9800"
          strokeWidth={1}
          dash={[3, 3]}
        />
      );

      // Right margin strip - on all pages (document edge)
      marginGuides.push(
        <Rect
          key={`margin-right-${pageIndex}`}
          x={canvasWidth}
          y={pageY}
          width={MARGIN_PX}
          height={singlePageHeight}
          fill="rgba(255, 193, 7, 0.2)" // Light amber overlay
          stroke="#ff9800"
          strokeWidth={1}
          dash={[3, 3]}
        />
      );
    }
  }

  // Add footer area rendering, only render on last page
  const footerAreas = [];
  if (showFooter && !previewMode) {
    const singlePageHeight = canvasHeight / totalPages;
    const pageY = (footerPage || 0) * singlePageHeight;
    const footerY = pageY + singlePageHeight - footerHeight;

    // Footer background
    footerAreas.push(
      <Rect
        key={`footer-bg`}
        x={0}
        y={footerY}
        width={canvasWidth}
        height={footerHeight}
        fill="rgba(240, 240, 240, 0.8)"
        stroke="#cccccc"
        strokeWidth={1}
        dash={[5, 5]}
      />
    );

    // Footer resize handle (top border of footer)
    footerAreas.push(
      <Rect
        key={`footer-resize`}
        x={0}
        y={footerY - 5}
        width={canvasWidth}
        height={10}
        fill="rgba(0, 100, 200, 0.3)"
      />
    );

    footerAreas.push(
      <Rect
        key={`footer-resize-indicator`}
        x={canvasWidth / 2 - 30}
        y={footerY - 3}
        width={60}
        height={6}
        fill="#0066cc"
        cornerRadius={3}
        cursor="ns-resize"
        onMouseEnter={() => {
          const stage = stageRef.current;
          if (stage) {
            stage.container().style.cursor = 'ns-resize';
          }
        }}
        onMouseLeave={() => {
          const stage = stageRef.current;
          if (stage) {
            stage.container().style.cursor = 'default';
          }
        }}
        onMouseDown={(e) => {
          if (!onFooterHeightChange) return;

          // Prevent event propagation
          e.evt?.stopPropagation?.();
          e.evt?.preventDefault?.();
          e.cancelBubble = true;

          const stage = e.target.getStage();
          if (!stage) return;

          const startY = stage.getPointerPosition()?.y || 0;
          const startHeight = footerHeight;

          const handleMouseMove = () => {
            const currentY = stage.getPointerPosition()?.y || 0;
            const deltaY = startY - currentY; // Invert for intuitive dragging
            const newHeight = Math.max(40, Math.min(500, startHeight + deltaY));
            onFooterHeightChange(newHeight);
          };

          const handleMouseUp = () => {
            stage.off('mousemove', handleMouseMove);
            stage.off('mouseup', handleMouseUp);
          };

          stage.on('mousemove', handleMouseMove);
          stage.on('mouseup', handleMouseUp);
        }}
      />
    );
  }

  return (
    <Box
      ref={(node) => {
        setNodeRef(node as HTMLDivElement);
        containerRef.current = node as HTMLDivElement | null;
      }}
      id="label-canvas"
      tabIndex={0} // Make the canvas focusable for keyboard events
      sx={{
        flex: 1,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        position: 'relative',
        overflow: 'auto', // Enable scrolling
        backgroundColor: '#fff',
        outline: 'none', // Remove focus outline
        ...(noPadding ? {} : { paddingTop: 2, paddingBottom: 2 }),
      }}
    >
      <Paper
        elevation={3}
        sx={{
          position: 'relative',
          marginLeft: position.x > 0 ? `${position.x}px` : 0,
          marginTop: position.y > 0 ? `${position.y}px` : 0,
          marginBottom: 8,
          width: showMargins
            ? (canvasWidth + TOTAL_MARGIN_EXPANSION) * scale
            : canvasWidth * scale,
          height: showMargins
            ? (canvasHeight + TOTAL_MARGIN_EXPANSION) * scale
            : canvasHeight * scale,
          overflow: 'hidden',
          borderRadius: 1,
          transition: 'box-shadow 0.3s ease',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <Stage
          ref={stageRef}
          width={
            showMargins
              ? (canvasWidth + TOTAL_MARGIN_EXPANSION) * scale
              : canvasWidth * scale
          }
          height={
            showMargins
              ? (canvasHeight + TOTAL_MARGIN_EXPANSION) * scale
              : canvasHeight * scale
          }
          onMouseDown={handleStageMouseDown}
          onMouseMove={handleStageMouseMove}
          onMouseUp={handleStageMouseUp}
          onTouchStart={handleStageMouseDown}
          onTouchMove={handleStageMouseMove}
          onTouchEnd={handleStageMouseUp}
          scaleX={scale}
          scaleY={scale}
        >
          <Layer
            ref={layerRef}
            x={showMargins ? MARGIN_PX : 0}
            y={showMargins ? MARGIN_PX : 0}
          >
            <Rect
              id="background-rect"
              x={0}
              y={0}
              width={canvasWidth}
              height={canvasHeight}
              fill="#ffffff"
            />

            {gridLines}
            {marginGuides}
            {pageBreakLines}
            {footerAreas}

            {elements.map((element) =>
              isGroupElement(element) ? (
                <GroupRenderer
                  key={element.id}
                  group={element}
                  isSelected={
                    selectedElement?.id === element.id ||
                    selectedElements.includes(element)
                  }
                  onSelect={() => {
                    if (previewMode) return;
                    setSelectedElement(element);
                    setSelectedElements([element]);
                  }}
                  onChange={(newAttrs) => {
                    const constrainedAttrs = handleBoundaryConstraints(
                      element,
                      newAttrs
                    );
                    onUpdateElement(element.id, constrainedAttrs);
                  }}
                  previewMode={previewMode}
                  previewData={previewData}
                />
              ) : (
                <ElementRenderer
                  key={element.id}
                  element={element}
                  isSelected={
                    selectedElement?.id === element.id ||
                    selectedElements.includes(element)
                  }
                  onSelect={() => {
                    if (previewMode) return;
                    setSelectedElement(element);
                    setSelectedElements([element]);
                  }}
                  onChange={(newAttrs) => {
                    const constrainedAttrs = handleBoundaryConstraints(
                      element,
                      newAttrs
                    );
                    onUpdateElement(element.id, constrainedAttrs);
                  }}
                  previewMode={previewMode}
                  previewData={previewData}
                  canvasWidth={canvasWidth}
                  canvasHeight={canvasHeight}
                  showFooter={showFooter}
                  footerHeight={footerHeight}
                />
              )
            )}

            {selectionBox && (
              <Rect
                x={selectionBox.x}
                y={selectionBox.y}
                width={selectionBox.width}
                height={selectionBox.height}
                fill="rgba(33, 150, 243, 0.1)"
                stroke="#2196f3"
                strokeWidth={1}
              />
            )}
          </Layer>
        </Stage>
      </Paper>

      {/* Floating Page Management Buttons */}
      {!previewMode && (onAddPage || onRemovePage) && (
        <Box
          sx={{
            position: 'absolute',
            top: `${
              (showMargins
                ? (canvasHeight + TOTAL_MARGIN_EXPANSION) * scale
                : canvasHeight * scale) +
              (position.y > 0 ? position.y : 0) +
              20
            }px`,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 2,
            zIndex: 1000,
          }}
        >
          {/* Add Page Button */}
          {onAddPage && (
            <FBOButton
              variant="primary"
              color="positive"
              icon="FBOAddCircle"
              data-qa="add-page-button"
              onClick={onAddPage}
              sx={{
                borderRadius: '50%',
                padding: 0,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              Add Page
            </FBOButton>
          )}

          {/* Remove Page Button */}
          {onRemovePage && totalPages > 1 && (
            <FBOButton
              variant="primary"
              color="negative"
              icon="FBODeleteSmall"
              data-qa="remove-page-button"
              onClick={onRemovePage}
              sx={{
                borderRadius: '50%',
                padding: 0,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              Remove Page
            </FBOButton>
          )}
        </Box>
      )}

      {isDragging && !previewMode && (
        <Box
          sx={{
            position: 'absolute',
            top: position.y > 0 ? position.y : 0,
            left: position.x > 0 ? position.x : 0,
            width: canvasWidth * scale,
            height: canvasHeight * scale,
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            zIndex: 10,
            pointerEvents: 'none',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 1,
          }}
        />
      )}
    </Box>
  );
};

export default DesignerCanvas;
